import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { MUIDataTable, MUIDataTableColumnDef, SectionHeader } from 'web-library';
import {
  AccessConfigDetails,
  TargetRoleMode,
  UserGroupAccessOption,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';

import UserGroupAccessOptionsDataLevel from 'web-library/components/UserGroupAccessOptionsDataLevel';

interface TableColumnsProps {
  dropdownList: UserGroupAccessOption[][];
  t: TFunction;
  functionListArrayWithSorting: string[][];
  defaultSelectedDataAccessLevel: string[][];
  setSelectedAccessOption: Dispatch<SetStateAction<string[][]>>;
  isEditable: boolean;
}

const tableColumns = ({
  dropdownList,
  t,
  functionListArrayWithSorting,
  defaultSelectedDataAccessLevel,
  setSelectedAccessOption,
  isEditable,
}: TableColumnsProps): MUIDataTableColumnDef[] => [
  {
    name: 'displayName',
    label: t('admin->>UserGroups.Function'),
    options: {
      sort: false,
      filter: false,
      setCellProps: () => ({ style: { width: '50%' } }),
      customBodyRenderLite: (dataIndex: number) => <>{functionListArrayWithSorting[dataIndex]}</>,
    },
  },
  {
    name: 'dataAccessLevel',
    label: t('admin->>UserGroups.DataAccessLevel'),
    options: {
      sort: false,
      filter: false,
      setCellProps: () => ({ style: { width: '50%' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <UserGroupAccessOptionsDataLevel
          accessOptions={dropdownList[dataIndex]}
          selectedAccessOptions={defaultSelectedDataAccessLevel[dataIndex]}
          valueTitle={`content[${dataIndex}].selectedAccessOptions`}
          placeholder={t('admin->>UserGroups.SelectDataAccessLevelPlaceholder')}
          onChangeHandler={(value) => {
            setSelectedAccessOption((prevState) => {
              // eslint-disable-next-line no-param-reassign
              prevState[dataIndex] = value;
              return prevState;
            });
          }}
          editable={isEditable}
        />
      ),
    },
  },
];

interface FunctionPermissionAccessOptionsDataTableProps {
  accessConfigurationListingData: AccessConfigDetails;
  selectedInstitution: { key: string; value: string };
  selectedAccessOption: string[][];
  setSelectedAccessOption: Dispatch<SetStateAction<string[][]>>;
  targetRoleMode: TargetRoleMode;
  isEditable?: boolean;
}

const AccessConfigurationListingDataTable = ({
  accessConfigurationListingData,
  selectedInstitution,
  selectedAccessOption,
  setSelectedAccessOption,
  targetRoleMode,
  isEditable = true,
}: FunctionPermissionAccessOptionsDataTableProps) => {
  const { t } = useTranslation();

  const functionListArray: string[][] = [];

  Object.keys(accessConfigurationListingData.authorities).forEach((obj) =>
    functionListArray.push([obj])
  );

  const functionListArrayWithSorting = [...functionListArray].sort();// NOSONAR

  const authoritiesEntry = [];

  Object.entries(accessConfigurationListingData.authorities).forEach((obj) =>
    authoritiesEntry.push([obj])
  );

  const dropdownUuidDisplayList: string[][][] = [];

  Object.entries(accessConfigurationListingData.authorities)
    .sort()// NOSONAR
    .forEach((authoritiesDetailsValue) => {
      if (authoritiesDetailsValue[0] === 'Registration') {
        const accessOption = { ...authoritiesDetailsValue[1].accessOptions };
        delete accessOption['GCE A-Level'];
        delete accessOption['GCE N(A)-Level'];
        delete accessOption['GCE N(T)-Level'];
        delete accessOption['GCE O-Level'];
        accessOption['GCE Levels'] = 'gceLevelsUuid';
        dropdownUuidDisplayList.push(Object.entries(accessOption));
      } else {
        dropdownUuidDisplayList.push(Object.entries(authoritiesDetailsValue[1].accessOptions));
      }
    });

  const dropdownList: UserGroupAccessOption[][] = [];

  dropdownUuidDisplayList.forEach((dropdown) => {
    const featureDropdownList: UserGroupAccessOption[] = [];
    dropdown.forEach((a) => {
      featureDropdownList.push({
        uuid: a[1],
        displayName: a[0],
      });
    });
    dropdownList.push(featureDropdownList);
  });

  const result: string[][] = [];

  const accessMatrixOption =
    accessConfigurationListingData.accessMatrixConfigurations[selectedInstitution.key]
      ?.accessMatrix;

  Object.keys(accessConfigurationListingData.authorities)
    .sort()// NOSONAR
    .forEach((authority) => {
      if (!accessMatrixOption?.[authority] || accessMatrixOption?.[authority].length <= 0) {
        result.push([]);
      } else if (authority === 'Registration') {
        // Aggregate GCE Levels for registration authority
        let gceLevelAdded = false;
        const processedGCE = accessMatrixOption?.[authority].map((accessLevel) => {
          if (accessLevel.includes('GCE')) {
            if (!gceLevelAdded) {
              gceLevelAdded = true;
              return 'gceLevelsUuid';
            }
            return '';
          }
          return accessConfigurationListingData.authorities[authority]?.accessOptions[accessLevel];
        });

        result.push(processedGCE.filter((value) => value !== ''));
      } else {
        result.push(
          accessMatrixOption?.[authority].map(
            (accessLevel) =>
              accessConfigurationListingData.authorities[authority]?.accessOptions[accessLevel]
          )
        );
      }
    });

  useEffect(() => {
    setSelectedAccessOption(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitution, selectedInstitution.key, setSelectedAccessOption]);

  if (selectedAccessOption.length <= 0) {
    return <></>;
  }

  return (
    <>
      <SectionHeader title={t('admin->>UserGroups.FunctionAccessDetails')} />
      {accessConfigurationListingData && (
        <MUIDataTable
          key={selectedInstitution.key && targetRoleMode}
          columns={tableColumns({
            dropdownList,
            t,
            functionListArrayWithSorting,
            defaultSelectedDataAccessLevel: result,
            setSelectedAccessOption,
            isEditable,
          })}
          data={functionListArrayWithSorting}
        />
      )}
    </>
  );
};

export default AccessConfigurationListingDataTable;
